/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */
/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

// useGetCountryCode.ts
import countries from 'i18n-iso-countries';

const supportedLanguages = ['de', 'en', 'nl', 'it', 'fr', 'es', 'sk', 'cs'] as const;

const localeLoaders: Record<(typeof supportedLanguages)[number], () => Promise<any>> = {
    de: () => import('i18n-iso-countries/langs/de.json'),
    en: () => import('i18n-iso-countries/langs/en.json'),
    nl: () => import('i18n-iso-countries/langs/nl.json'),
    it: () => import('i18n-iso-countries/langs/it.json'),
    fr: () => import('i18n-iso-countries/langs/fr.json'),
    es: () => import('i18n-iso-countries/langs/es.json'),
    sk: () => import('i18n-iso-countries/langs/sk.json'),
    cs: () => import('i18n-iso-countries/langs/cs.json'),
};

let localesReady: Promise<void> | null = null;

async function ensureLocalesRegistered() {
    if (!localesReady) {
        localesReady = Promise.all(
            supportedLanguages.map(async (lang) => {
                const mod = await localeLoaders[lang]();
                const pack = 'default' in mod ? mod.default : mod; // Vite JSON -> default export
                countries.registerLocale(pack);
            })
        ).then(() => undefined);
    }
    return localesReady;
}

const normalizeCountryValue = (countryValue: string): string => {
    return countryValue.trim();
};

const parseAlpha2FromCountryValue = (countryValue: string): string | null => {
    const normalizedValue = normalizeCountryValue(countryValue);
    const uppercaseValue = normalizedValue.toUpperCase();

    if (/^[A-Z]{2}$/.test(uppercaseValue)) {
        return uppercaseValue;
    }

    for (const lang of supportedLanguages) {
        const code = countries.getAlpha2Code(normalizedValue, lang);
        if (code) {
            return code;
        }
    }

    return null;
};

export const getCountryCodeFromCountryName = async (
    countryName: string
): Promise<string | null> => {
    await ensureLocalesRegistered();
    return parseAlpha2FromCountryValue(countryName);
};

export const getEnglishCountryNameFromAnyCountryValue = async (
    countryValue: string,
    fallbackCountryName: string = 'Switzerland'
): Promise<string> => {
    await ensureLocalesRegistered();
    const alpha2Code = parseAlpha2FromCountryValue(countryValue);

    if (!alpha2Code) {
        return fallbackCountryName;
    }

    return countries.getName(alpha2Code, 'en') || fallbackCountryName;
};

const useGetCountryCode = () => {
    const getCountryCodeFromCountryNameHandler = async (countryName: string): Promise<string | null> => {
        return getCountryCodeFromCountryName(countryName);
    };

    return {getCountryCodeFromCountryName: getCountryCodeFromCountryNameHandler};
};

export default useGetCountryCode;
